/**
 * Shared Sidebar Component
 * 
 * Reusable sidebar for all pages with language switcher
 */

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  
  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    setShowLanguageModal(false);
  };

  const tabs = [
    { id: 'data', label: t('nav.dataExtractor'), icon: 'fa-spider', path: '/' },
    { id: 'templates', label: t('nav.templates'), icon: 'fa-layer-group', path: '/templates' },
    { id: 'jobs', label: t('nav.scheduledJobs'), icon: 'fa-briefcase', path: '/jobs' },
    { id: 'history', label: t('nav.history'), icon: 'fa-clock-rotate-left', path: '/history' },
    { id: 'management', label: t('nav.crawlerMonitoring'), icon: 'fa-gears', path: '/monitor' }
  ];

  return (
    <>
      <aside className="w-16 bg-gradient-to-b from-dark-300 to-dark-200 flex flex-col items-center py-6 shadow-2xl">
        {/* Logo */}
        <div className="mb-8">
          <NavLink to="/">
            <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity cursor-pointer">
              <i className="fa-solid fa-robot text-white text-lg"></i>
            </div>
          </NavLink>
        </div>
        
        {/* Main Navigation */}
        <nav className="flex-1 flex flex-col space-y-2 w-full px-2">
          {tabs.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.path}
              className={({ isActive }) => `group ${isActive ? 'sidebar-icon-active' : 'sidebar-icon'}`}
            >
              {({ isActive }) => (
                <>
                  <i className={`fa-solid ${tab.icon} ${isActive ? 'text-primary-500' : 'text-light-200 group-hover:text-white'} text-lg`}></i>
                  <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{tab.label}</span>
                </>
              )}
            </NavLink>
          ))}
          
          <a 
            href={`${window.location.protocol}//${window.location.hostname}:8000/docs`}
            target="_blank" 
            rel="noopener noreferrer"
            className="group sidebar-icon"
          >
            <i className="fa-solid fa-file-code text-light-200 group-hover:text-white text-lg"></i>
            <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{t('nav.apiDocs')}</span>
          </a>
        </nav>
        
        {/* Footer Icons */}
        <div className="space-y-2 w-full px-2">
          {/* Language Switcher Button */}
          <button 
            onClick={() => setShowLanguageModal(true)}
            className="group sidebar-icon"
          >
            <i className="fa-solid fa-language text-light-200 group-hover:text-white text-lg"></i>
            <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{t('sidebar.tooltip.language')}</span>
          </button>
          
          <button className="group sidebar-icon">
            <i className="fa-solid fa-bell text-light-200 group-hover:text-white text-lg"></i>
            <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{t('sidebar.tooltip.notifications')}</span>
          </button>
          <NavLink
            to="/settings"
            className={({ isActive }) => `group ${isActive ? 'sidebar-icon-active' : 'sidebar-icon'}`}
          >
            {({ isActive }) => (
              <>
                <i className={`fa-solid fa-gears ${isActive ? 'text-primary-500' : 'text-light-200 group-hover:text-white'} text-lg`}></i>
                <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{t('sidebar.tooltip.settings')}</span>
              </>
            )}
          </NavLink>
          <button 
            onClick={handleLogout}
            className="group sidebar-icon"
          >
            <i className="fa-solid fa-right-from-bracket text-light-200 group-hover:text-white text-lg"></i>
            <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">{t('sidebar.tooltip.logout')}</span>
          </button>
          <div className="pt-2 border-t border-dark-100">
            <NavLink to="/profile" className="block relative group">
              {user?.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.first_name}
                  className="w-10 h-10 rounded-lg object-cover hover:opacity-80 transition-opacity cursor-pointer"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-800 flex items-center justify-center text-white font-bold hover:opacity-80 transition-opacity cursor-pointer">
                  {user?.first_name?.[0] || 'U'}
                </div>
              )}
              <span className="absolute left-16 bg-dark-200 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {user?.first_name || t('sidebar.tooltip.profile')}
              </span>
            </NavLink>
          </div>
        </div>
      </aside>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]" onClick={() => setShowLanguageModal(false)}>
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-dark-300">{t('language.select')}</h3>
              <button onClick={() => setShowLanguageModal(false)} className="text-dark-100 hover:text-dark-300 transition-colors">
                <i className="fa-solid fa-xmark text-2xl"></i>
              </button>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => changeLanguage('en')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                  i18n.language === 'en' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-light-200/30 hover:border-primary-500 hover:bg-light-100'
                }`}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🇬🇧</span>
                  <div>
                    <div className="font-bold text-dark-300">{t('language.english')}</div>
                    <div className="text-xs text-dark-100">English</div>
                  </div>
                </div>
                {i18n.language === 'en' && (
                  <i className="fa-solid fa-check text-primary-500 text-xl"></i>
                )}
              </button>
              
              <button
                onClick={() => changeLanguage('de')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                  i18n.language === 'de' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-light-200/30 hover:border-primary-500 hover:bg-light-100'
                }`}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🇩🇪</span>
                  <div>
                    <div className="font-bold text-dark-300">{t('language.german')}</div>
                    <div className="text-xs text-dark-100">Deutsch</div>
                  </div>
                </div>
                {i18n.language === 'de' && (
                  <i className="fa-solid fa-check text-primary-500 text-xl"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
